import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2, Upload } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";

const SEGMENT_MS = 8 * 60 * 1000;
const mm = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// Records in independent ~8-minute webm segments; each is uploaded and transcribed while recording continues.
export const MeetingRecorder = ({ meetingId, onTranscript }) => {
  const [rec, setRec] = useState(false);
  const [secs, setSecs] = useState(0);
  const [pending, setPending] = useState(0);
  const st = useRef({});
  const fileRef = useRef(null);

  const upload = async (blob, seq, name = "seg.webm") => {
    setPending((n) => n + 1);
    const fd = new FormData();
    fd.append("file", blob, name); fd.append("seq", String(seq));
    try { const r = await api.meetingAudio(meetingId, fd); if (r.transcript !== undefined) onTranscript(r.transcript); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تفريغ مقطع صوتي"); }
    finally { setPending((n) => n - 1); }
  };

  const startSegment = () => {
    const s = st.current;
    const mr = new MediaRecorder(s.stream, MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? { mimeType: "audio/webm;codecs=opus", audioBitsPerSecond: 32000 } : undefined);
    const chunks = [];
    const seq = s.seq++;
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = () => { const b = new Blob(chunks, { type: mr.mimeType || "audio/webm" }); upload(b, seq, `seg${seq}.${(mr.mimeType || "").includes("mp4") ? "mp4" : "webm"}`); };
    mr.start(1000);
    s.mr = mr;
    s.cut = setTimeout(() => { if (s.active) { mr.stop(); startSegment(); } }, SEGMENT_MS);
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      st.current = { stream, seq: Date.now() % 1e6, active: true };
      startSegment();
      setSecs(0); setRec(true);
      st.current.tick = setInterval(() => setSecs((x) => x + 1), 1000);
    } catch { toast.error("لم يُسمح بالوصول إلى الميكروفون"); }
  };
  const stop = () => {
    const s = st.current;
    s.active = false;
    clearTimeout(s.cut); clearInterval(s.tick);
    if (s.mr && s.mr.state !== "inactive") s.mr.stop();
    s.stream?.getTracks().forEach((t) => t.stop());
    setRec(false);
  };
  useEffect(() => () => { if (st.current.active) stop(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="meeting-recorder">
      {rec ? (
        <Button onClick={stop} className="gap-2 rounded-full bg-rose-600 hover:bg-rose-700" data-testid="meet-rec-stop">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" /></span>
          <Square className="h-3.5 w-3.5" /> إيقاف التسجيل <span className="tabular-nums" dir="ltr">{mm(secs)}</span></Button>
      ) : (
        <Button onClick={start} className="gap-2 rounded-full bg-slate-900 hover:bg-slate-800" data-testid="meet-rec-start"><Mic className="h-4 w-4" /> تسجيل الاجتماع</Button>
      )}
      <input ref={fileRef} type="file" accept="audio/*,video/mp4" className="hidden" data-testid="meet-audio-file"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) { if (f.size > 24 * 1024 * 1024) toast.error("الملف أكبر من 24 ميجابايت"); else upload(f, Date.now() % 1e6, f.name); } e.target.value = ""; }} />
      <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-1.5 rounded-full" data-testid="meet-audio-upload"><Upload className="h-4 w-4" /> رفع تسجيل</Button>
      {pending > 0 && <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid="meet-transcribing"><Loader2 className="h-3.5 w-3.5 animate-spin" /> جارٍ التفريغ…</span>}
    </div>
  );
};
